
import { useState, useEffect } from "react";
import { MessageTone } from "@/types/message";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RefreshCw, MessageCircle } from "lucide-react";
import { useSubscriptionFeatures } from "@/hooks/use-subscription-features";
import { useNavigate } from "react-router-dom";
import ToneSelector from "./dialog/ToneSelector";
import CustomInstructionsInput from "./dialog/CustomInstructionsInput";
import UpgradeAlert from "./dialog/UpgradeAlert";

interface RegenerationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRegenerate: (tone: MessageTone, customInstructions?: string) => void;
  initialTone: MessageTone;
  initialInstructions?: string;
  isRegenerating: boolean;
  isInitialMessage?: boolean;
}

const MAX_CHARS = 500;

const RegenerationDialog = ({ 
  isOpen, 
  onClose, 
  onRegenerate,
  initialTone,
  initialInstructions = "",
  isRegenerating,
  isInitialMessage = false
}: RegenerationDialogProps) => {
  const [selectedTone, setSelectedTone] = useState<MessageTone>(initialTone);
  const [customInstructions, setCustomInstructions] = useState(initialInstructions);
  const { getAvailableTones, isSubscribed } = useSubscriptionFeatures();
  const navigate = useNavigate();
  
  const charCount = customInstructions.length;
  const isOverLimit = charCount > MAX_CHARS;
  const showCustomInstructions = selectedTone === "custom";
  
  // Get available tones for current user
  const availableToneValues = getAvailableTones();

  // Enhanced debugging for production
  console.log("=== REGENERATION DIALOG DEBUG ===");
  console.log("Environment:", window.location.hostname);
  console.log("Dialog state:", { 
    isOpen,
    isSubscribed, 
    availableToneValues, 
    selectedTone,
    initialTone,
    canAccessSelected: availableToneValues.includes(selectedTone)
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      console.log("Dialog opened, setting initial tone:", initialTone);
      console.log("Available tones on open:", availableToneValues);
      
      // Always set the initial tone, even if not accessible (for UI consistency)
      setSelectedTone(initialTone);
      setCustomInstructions(initialInstructions || "");
      
      console.log("State after reset:", {
        selectedTone: initialTone,
        customInstructions: initialInstructions || ""
      });
    }
  }, [isOpen, initialTone, initialInstructions]);

  const handleToneChange = (value: string) => {
    const newTone = value as MessageTone;
    console.log("=== TONE CHANGE EVENT ===");
    console.log("Previous tone:", selectedTone);
    console.log("New tone:", newTone);
    console.log("Available tones:", availableToneValues);
    console.log("Can access new tone:", availableToneValues.includes(newTone));
    
    // Always update the selected tone - validation happens on submit
    setSelectedTone(newTone);
    
    console.log("Tone updated to:", newTone);
  };

  const handleSubmit = () => {
    console.log("=== SUBMIT ATTEMPT ===");
    console.log("Selected tone:", selectedTone);
    console.log("Available tones:", availableToneValues);
    console.log("Is subscribed:", isSubscribed);
    console.log("Can access tone:", availableToneValues.includes(selectedTone));
    console.log("Is over limit:", isOverLimit);
    console.log("Custom instructions:", selectedTone === "custom" ? customInstructions : "N/A");
    
    // Check character limit first
    if (isOverLimit) {
      console.log("Submit blocked: Character limit exceeded");
      return;
    }
    
    // Check custom instructions if custom tone selected
    if (selectedTone === "custom" && !customInstructions.trim()) {
      console.log("Submit blocked: Custom tone selected but no instructions provided");
      return;
    }
    
    // Check if user can access the selected tone
    if (!availableToneValues.includes(selectedTone)) {
      console.log("Submit blocked: Tone not available for user subscription level");
      return;
    }
    
    console.log("=== SUBMITTING REGENERATION ===");
    console.log("Final tone:", selectedTone);
    console.log("Final instructions:", selectedTone === "custom" ? customInstructions : undefined);
    
    onRegenerate(
      selectedTone, 
      selectedTone === "custom" ? customInstructions : undefined
    );
  };

  const handleUpgradeClick = () => {
    console.log("Upgrade button clicked, navigating to pricing");
    navigate('/pricing');
    onClose();
  };

  // Check if submit should be disabled
  const isSubmitDisabled = 
    isRegenerating || 
    (selectedTone === "custom" && !customInstructions.trim()) || 
    isOverLimit ||
    (!availableToneValues.includes(selectedTone));

  console.log("=== SUBMIT BUTTON STATE ===");
  console.log("Submit disabled:", isSubmitDisabled);
  console.log("Reasons:", {
    isRegenerating,
    customToneWithoutInstructions: selectedTone === "custom" && !customInstructions.trim(),
    isOverLimit,
    toneNotAvailable: !availableToneValues.includes(selectedTone)
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            {isInitialMessage ? "Change Message Tone" : "Regenerate Response"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <ToneSelector
            selectedTone={selectedTone}
            onToneChange={handleToneChange}
            availableToneValues={availableToneValues}
          />
          
          {!isSubscribed && !availableToneValues.includes(selectedTone) && (
            <UpgradeAlert onUpgradeClick={handleUpgradeClick} />
          )}
          
          {showCustomInstructions && (
            <CustomInstructionsInput
              customInstructions={customInstructions}
              onCustomInstructionsChange={setCustomInstructions}
              maxChars={MAX_CHARS}
            />
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isRegenerating}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitDisabled}
            className="gap-2"
          >
            {isRegenerating ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                {isInitialMessage ? "Changing tone..." : "Regenerating..."}
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4" />
                {isInitialMessage ? "Change Tone" : "Regenerate Response"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegenerationDialog;
